import { useEffect, useRef, useState } from 'react';
import { INK_COLORS } from '../../store/illustrations.js';

// The meeting's controls, behind one button.
//
// Everything an advisor does to the meeting rather than in it lives in a menu
// under three lines at the end of the step bar (Jimmy, 2026-09-29), so the
// client's view is the steps and the slide. The keys still work: D picks the
// marker up and E turns Adjust on, without opening anything. While the marker
// is in hand its colours, Undo and Done sit beside the menu, because an
// advisor mid-illustration should not have to open a menu to change pen.

function ClearConfirm({ open, onCancel, onConfirm, title }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog className="conv-confirm" ref={ref} onCancel={e => { e.preventDefault(); onCancel(); }}>
      <h3>Clear this drawing?</h3>
      <p>
        {title
          ? `Every mark on "${title}" is removed. Other drawings are not affected.`
          : 'Every mark on this page is removed. Other drawings are not affected.'}
      </p>
      <div className="conv-actions" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="conv-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="conv-btn is-danger" onClick={onConfirm}>Clear drawing</button>
      </div>
    </dialog>
  );
}

function MenuIcon() {
  return (
    <svg width="18" height="14" viewBox="0 0 18 14" aria-hidden="true">
      <rect y="0" width="18" height="2" rx="1" fill="currentColor" />
      <rect y="6" width="18" height="2" rx="1" fill="currentColor" />
      <rect y="12" width="18" height="2" rx="1" fill="currentColor" />
    </svg>
  );
}

/**
 * The marker's controls while it is in hand: colours, Undo and Done, floating
 * at the top left of the white area beside the page (Jimmy, 2026-10-02).
 *
 * The same card floats on a whiteboard too (Jimmy, 2026-10-04), with Clear in
 * place of Done, because there the marker is always in hand. On a phone or a
 * tablet held upright it lies in a row above the page instead of a column
 * beside it, which is where the screen has room for it (conversation.css).
 */
export function DrawWidget({ color, onColor, canUndo, onUndo, onDone, onClear, canClear = false }) {
  return (
    <div className="conv-draw-widget no-print" role="toolbar" aria-label="Marker">
      <fieldset className="conv-palette">
        <legend className="sr-only">Marker colour</legend>
        {INK_COLORS.map(c => (
          <span className="conv-swatch" key={c.key} style={{ '--swatch': c.hex }}>
            <input
              type="radio"
              name="conv-ink"
              value={c.hex}
              checked={color === c.hex}
              onChange={() => onColor(c.hex)}
              aria-label={c.label}
            />
          </span>
        ))}
      </fieldset>
      <button type="button" className="conv-btn is-small" onClick={onUndo} disabled={!canUndo}>Undo</button>
      {onClear ? <button type="button" className="conv-btn is-small" onClick={onClear} disabled={!canClear}>Clear</button> : null}
      {onDone ? <button type="button" className="conv-btn is-small is-primary" onClick={onDone}>Done <kbd>Esc</kbd></button> : null}
    </div>
  );
}

/**
 * The same place says when Adjust is on. Without it the "Leave out" links and
 * the list of what was left out look like part of the page the client sees,
 * with no sign of how to put them away (Amanda Cook, 2026-10-02).
 */
export function AdjustWidget({ onDone }) {
  return (
    <div className="conv-draw-widget conv-adjust-widget no-print" role="status" aria-label="Edit mode">
      <strong>Edit mode</strong>
      <button type="button" className="conv-btn is-small is-primary" onClick={onDone}>Done <kbd>E</kbd></button>
    </div>
  );
}

export default function MeetingMenu({
  drawing,
  readOnly = false,
  canDraw = !readOnly,
  canAdjust = !readOnly,
  drawLocked = false,
  onPair = null,
  paired = false,
  onStatements = null,
  statementsLabel = 'Statements',
  onBrief = null,
  link = null,
  onToggleDraw,
  color,
  onColor,
  canUndo,
  onUndo,
  canClear,
  onClear,
  clearOpen,
  onClearCancel,
  onClearConfirm,
  clearTitle,
  onWhiteboard,
  onDrawer,
  illustrationCount,
  status,
  adjusting = false,
  onToggleAdjust,
  triangle = null,
  onToggleTriangle,
  onBack,
  // With the whiteboard always open beside the brief the marker is always in
  // hand there, so Draw picks it up for the brief instead (Jimmy, 2026-10-19).
  boardOnly = false,
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const outside = e => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    const key = e => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', key, true);
    };
  }, [open]);

  const act = fn => () => { setOpen(false); fn?.(); };

  return (
    <div className="conv-menu-wrap" ref={wrap}>
      <button
        type="button"
        className="conv-menu-btn"
        aria-label="Meeting menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
      >
        <MenuIcon />
        {/* On a phone or a tablet, whether the other device is there. */}
        {link ? <span className={`conv-menu-badge${link.on ? ' is-on' : ''}`} aria-hidden="true" /> : null}
      </button>

      {open && (
        <div className="conv-menu" role="menu" aria-label="Meeting menu">
          {/* On a whiteboard the marker is always in hand. */}
          <button type="button" role="menuitemcheckbox" aria-checked={drawing} disabled={!canDraw || drawLocked} onClick={act(onToggleDraw)}>
            <span>{boardOnly
              ? (drawing ? 'Stop drawing on the brief' : 'Draw on the brief')
              : (drawing && !drawLocked ? 'Stop drawing' : 'Draw')}</span><kbd>D</kbd>
          </button>
          <button type="button" role="menuitemcheckbox" aria-checked={adjusting} disabled={!canAdjust} onClick={act(onToggleAdjust)}>
            <span>{adjusting ? 'Finish adjusting' : 'Adjust figures and findings'}</span><kbd>E</kbd>
          </button>
          {/* Only on the tax slide, where there is a triangle to show or not. */}
          {triangle !== null ? (
            <button type="button" role="menuitemcheckbox" aria-checked={triangle} disabled={readOnly} onClick={act(onToggleTriangle)}>
              <span>Show the tax triangle</span>
            </button>
          ) : null}
          <div className="conv-menu-sep" role="separator" />
          <button type="button" role="menuitem" disabled={!canUndo} onClick={act(onUndo)}>
            <span>Undo last mark</span>
          </button>
          <button type="button" role="menuitem" disabled={!canClear} onClick={act(onClear)}>
            <span>{boardOnly ? 'Clear this whiteboard' : 'Clear this page\'s drawing'}</span>
          </button>
          <button type="button" role="menuitem" disabled={!canDraw} onClick={act(onWhiteboard)}>
            <span>New whiteboard</span>
          </button>
          <button type="button" role="menuitem" onClick={act(onDrawer)}>
            <span>{illustrationCount ? `Illustrations (${illustrationCount})` : 'Illustrations'}</span>
          </button>
          {onPair || onStatements ? <div className="conv-menu-sep" role="separator" /> : null}
          {onPair ? (
            <button type="button" role="menuitem" onClick={act(onPair)}>
              <span>{paired ? 'iPad connected' : 'Connect an iPad'}</span>
            </button>
          ) : null}
          {/* On a phone or a tablet the page header is put away in the meeting. */}
          {onStatements ? (
            <button type="button" role="menuitem" onClick={act(onStatements)}>
              <span>{statementsLabel}</span>
            </button>
          ) : null}
          {/* The AI planning brief, where this environment has it. */}
          {onBrief ? (
            <button type="button" role="menuitem" onClick={act(onBrief)}>
              <span>Planning brief</span>
            </button>
          ) : null}
          {onBack ? (
            <>
              <div className="conv-menu-sep" role="separator" />
              <button type="button" role="menuitem" onClick={act(onBack)}>
                <span>Back to the questionnaire</span>
              </button>
            </>
          ) : null}
          {link ? <p className={`conv-menu-status is-link${link.on ? ' is-on' : ''}`}>{link.text}</p> : null}
          {status ? <p className="conv-menu-status">{status}</p> : null}
        </div>
      )}

      <ClearConfirm
        open={clearOpen}
        onCancel={onClearCancel}
        onConfirm={onClearConfirm}
        title={clearTitle}
      />
    </div>
  );
}
